import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { PublicationCode, PublicationCodes } from '@/scopes/common/domain/models'

const CatalogueDetailsContext = createContext<{
  isWinter: boolean
  publicationCode: string
  isPublicationCodeReady: boolean
  setPublicationCode: (value: string) => void
  /* isPublicationCodeReady defaults to true here so consumers used outside of
 CatalogueDetailsProvider (e.g. unit tests, Storybook) behave as before.
 CatalogueDetailsProvider itself starts unready until a page explicitly sets the code. */
}>({ isWinter: false, publicationCode: '', isPublicationCodeReady: true, setPublicationCode: () => {} })

function CatalogueDetailsProvider({ children }: { children: ReactNode }) {
  const [isWinter, setIsWinter] = useState<boolean>(false)
  const [publicationCode, setPublicationCodeState] = useState<string>('')
  const [isPublicationCodeReady, setIsPublicationCodeReady] = useState<boolean>(false)

  /* Consumers (e.g. useSearchQuery) must not fire API calls with the initial/stale
   publicationCode. Pages own the correct value and mark it ready once they set it. */
  const setPublicationCode = useCallback((value: string) => {
    setPublicationCodeState(value)
    setIsPublicationCodeReady(true)
  }, [])

  useEffect(() => {
    setIsWinter(PublicationCodes.isSunwebWinterPublicationCode(publicationCode as PublicationCode))
  }, [publicationCode])

  const value = useMemo(
    () => ({ isWinter, publicationCode, isPublicationCodeReady, setPublicationCode }),
    [isWinter, publicationCode, isPublicationCodeReady, setPublicationCode],
  )

  return (
    <CatalogueDetailsContext value={value}>
      {children}
    </CatalogueDetailsContext>
  )
}

export { CatalogueDetailsContext, CatalogueDetailsProvider }
